/* eslint-disable tailwindcss/no-custom-classname */
/* eslint-disable tailwindcss/no-arbitrary-value */
import React, { useState, useEffect } from 'react';
import proptypes from 'prop-types';
import {
  Button,
  Card,
  CardTitle,
  CardMedia,
  CardActions,
  CardBody,
  Link,
  Typography,
} from '@one-thd/sui-atomic-components';
import classnames from 'classnames';
import { ImpressionProvider, useImpression } from '@thd-olt-component-react/impression';
import { useThdCustomer } from '@thd-olt-functional/customer-information';
import { CreateAccountCardContent } from './CreateAccountCardContent';
import { SignInCardContent } from './SignInCardContent';

const { string } = proptypes;

function useUserInfo(customerName, customerEmail) {
  const [userInfo, setUserInfo] = useState({
    userName: customerName?.toLowerCase(),
    userEmail: customerEmail?.replace(/(.{2})(.*)(?=@)/, '$1***'),
  });

  useEffect(() => {
    if (!userInfo.userName || !userInfo.userEmail) {
      setUserInfo({
        userName: customerName?.toLowerCase(),
        userEmail: customerEmail?.replace(/(.{2})(.*)(?=@)/, '$1***'),
      });
    }
  }, []);

  return userInfo;
}

function useCardContent(userName, userEmail) {
  const [cardContent, setCardContent] = useState({
    signInTitle: null,
    signInLink: null,
  });

  useEffect(() => {
    if (!userEmail) {
      setCardContent({
        signInTitle: 'Benefits Down Every Aisle',
        signInLink: 'Sign In',
      });
    } else {
      setCardContent({
        signInTitle:
          userName && userName.toLowerCase() !== 'self'
            ? `Is That You, ${userName}?`
            : 'Keep Your Projects Going',
        signInLink:
          userName && userName.toLowerCase() !== 'self'
            ? `Not ${userName}?`
            : 'Not You?',
      });
    }
  }, [userName, userEmail]);

  return cardContent;
}

// eslint-disable-next-line max-len
const imageUrl = 'https://dam.thdstatic.com/content/production/-LmJOfYRgYkIbBZQbnK3hQ/BCslv86A3HxgIETDmewyVQ/downloadImageForWeb/Welcome-to-Home-Depot.Jpg';

const SignInComponent = ({
  customerName = null, customerEmail = null, schema = null, componentId = '', isB2C = false
}) => {
  const thdCustomer = useThdCustomer();
  // Use customerName/customerEmail if available, otherwise fall back to internal values
  const finalCustomerName = customerName || thdCustomer?.username;
  const finalCustomerEmail = customerEmail || thdCustomer?.email;

  const { userName, userEmail } = useUserInfo(finalCustomerName, finalCustomerEmail);
  const { signInTitle, signInLink } = useCardContent(userName, userEmail);

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('sign-in.ready');
  }, []);

  const { ref, clickID } = useImpression({
    data: {
      id: componentId,
      name: userEmail ? 'SignInPersist' : 'SignInCard',
      component: 'SignIn',
      position: 1,
      type: 'content',
    },
  });

  const cardClass = classnames(
    "sui-flex sui-justify-center sui-h-full",
    { "!sui-rounded-[12px]": isB2C }
  );

  return (
    <Card
      className={cardClass}
      data-component="SignIn"
      ref={ref}
      // eslint-disable-next-line react/no-unknown-property
      clickid={clickID}
    >
      <CardTitle
        header={(
          <Typography variant="h3">
            {/* Typography currently does not have text-transform: capitalize */}
            <span className="sui-capitalize">{signInTitle}</span>
          </Typography>
        )}
        disableActionSpacing
        action={(
          <Link
            variant="body-base"
            href="/auth/view/signin?redirect=/&ref=null"
            underline="always"
          >
            <span className="sui-capitalize">{signInLink}</span>
          </Link>
        )}
      />
      <CardMedia className={cardClass} src={imageUrl} alt="Sign In Card Banner" />
      <CardBody>
        {userEmail ? (
          <SignInCardContent userEmail={userEmail} schema={schema} />
        ) : (
          <CreateAccountCardContent />
        )}
      </CardBody>
      <CardActions position="center">
        <Button
          fullWidth
          variant="primary"
          role="button"
          tag="a"
          href="/auth/view/signin?redirect=/&ref=null"
        >
          {userEmail ? 'Sign In' : 'Create an Account'}
        </Button>
      </CardActions>
    </Card>
  );
};

SignInComponent.displayName = 'SignInComponent';

SignInComponent.propTypes = {
  customerName: string,
  customerEmail: string,
  schema: string,
  componentId: string,
  isB2C: proptypes.bool,
};

const SignIn = ({
  customerName = null,
  customerEmail = null,
  schema = null,
  componentId = '',
  isB2C = false
}) => {

  return (
    <ImpressionProvider
      data={{
        id: componentId,
        name: 'SignIn',
        component: 'SignIn',
        type: 'content',
        position: 1
      }}
    >
      <SignInComponent
        customerName={customerName}
        customerEmail={customerEmail}
        schema={schema}
        componentId={componentId}
        isB2C={isB2C}
      />
    </ImpressionProvider>
  );

};

SignIn.displayName = 'SignIn';

SignIn.propTypes = {
  customerName: string,
  customerEmail: string,
  schema: string,
  componentId: string,
  isB2C: proptypes.bool,
};

export { SignIn };
